import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { RadixMultiSelect } from '@/components/radix';
import { t } from 'i18next';
import { useProductCategories } from '@/hooks/api/pos/products/useProductCategories';
import { usePlaygroundFormRegistry } from '../../hooks/usePlaygroundFormRegistry';
import { DURATION_OPERATORS_WITHIN_AFTER } from '@/data/Marketing/marketingDurationOperators';
import DaySelector, { type DaySelectorLabel } from './DaySelector';
import {
    buildCompositeFromDaySelectorValue,
    DURATION_HOURS_TOUCHED,
    getDaySelectorValueFromComposite,
} from './durationPeriodUtils';

type ProductCategoryValue = string[] | string | number[];

interface ProductCategoryCompositeValues {
    categoryIds?: string[] | number[];
    days?: [number | null, number | null];
    selectedDurationFormat?: DaySelectorLabel;
}

interface ProductCategoryMultiSelectProps {
    value: ProductCategoryValue | null | undefined;
    compositeValues?: ProductCategoryCompositeValues;
    conditionInstanceId: string;
    hasPeriod?: boolean;
}

const ProductCategoryMultiSelect = ({
    value,
    compositeValues,
    conditionInstanceId,
    hasPeriod: explicitHasPeriod,
}: ProductCategoryMultiSelectProps) => {
    const { register } = usePlaygroundFormRegistry();
    const hasPeriod = explicitHasPeriod !== undefined ? explicitHasPeriod : !!compositeValues;
    // Fetch product categories
    const { data: categoriesData, isLoading } = useProductCategories(true);

    // Transform categories to options
    const categoryOptions: Array<{ label: string; value: string }> = [];
    if (categoriesData && Array.isArray(categoriesData)) {
        categoriesData.forEach((category: { id: number | string; name?: string }) => {
            categoryOptions.push({
                label: category.name || `Category ${category.id}`,
                value: String(category.id),
            });
        });
    }

    // Extract values from compositeValues or use defaults
    const categoryIds = hasPeriod ? compositeValues?.categoryIds || [] : value;
    const initialDuration = getDaySelectorValueFromComposite(compositeValues, {
        includeOperator: hasPeriod,
        defaultOperator: 'AFTER_HOURS',
    });

    const formik = useFormik({
        initialValues: {
            categoryIds: Array.isArray(categoryIds)
                ? categoryIds.map(String)
                : categoryIds
                  ? [String(categoryIds)]
                  : [],
            duration: initialDuration,
        },
        validationSchema: Yup.object().shape({
            categoryIds: Yup.array().of(Yup.string()).min(1, t('Marketing.CategoriesRequired')),
            duration: hasPeriod
                ? Yup.object().shape({
                      hours: Yup.number().test(
                          'required',
                          t('Marketing.DaysRequired'),
                          (val) => val !== null && val !== undefined,
                      ),
                  })
                : Yup.mixed().notRequired(),
        }),
        enableReinitialize: true,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: () => {
            // Validation handled automatically
        },
    });

    useEffect(() => {
        register(conditionInstanceId, {
            validate: async () => {
                const errors = await formik.validateForm();
                if (Object.keys(errors).length > 0) {
                    formik.setTouched({
                        categoryIds: true,
                        ...(hasPeriod ? { duration: DURATION_HOURS_TOUCHED } : {}),
                    });
                    return false;
                }
                return true;
            },
            getValues: () => {
                if (hasPeriod) {
                    return {
                        categoryIds: formik.values.categoryIds,
                        ...buildCompositeFromDaySelectorValue(formik.values.duration),
                    };
                }
                return formik.values.categoryIds;
            },
        });
        // Keep validator in registry on unmount so we can validate all at once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conditionInstanceId, formik.values, hasPeriod, register]);

    const handleCategoryChange = (selectedValues: Set<string>) => {
        formik.setFieldValue('categoryIds', Array.from(selectedValues));
    };

    const selectedValuesSet = new Set(formik.values.categoryIds || []);

    return (
        <div className={hasPeriod ? 'flex flex-col gap-4' : 'w-full'}>
            {/* Category Selection */}
            <div className="w-full min-w-full">
                <label className="block text-sm font-medium text-text-primary mb-2">
                    {t('Marketing.SelectProductCategories')}
                </label>
                <RadixMultiSelect
                    options={categoryOptions}
                    selectedValues={selectedValuesSet}
                    onSelectionChange={handleCategoryChange}
                    placeholder={t('Marketing.SelectProductCategories')}
                    textToDisplayWithCount={t('Marketing.CategoriesSelected')}
                    className="min-w-full"
                    disabled={isLoading}
                />
                {formik.touched.categoryIds && formik.errors.categoryIds && (
                    <div className="text-sm text-red-500 mt-1">
                        {typeof formik.errors.categoryIds === 'string'
                            ? formik.errors.categoryIds
                            : Array.isArray(formik.errors.categoryIds)
                              ? (formik.errors.categoryIds as string[]).join(', ')
                              : t('Marketing.CategoriesRequired')}
                    </div>
                )}
            </div>

            {/* Days Range Input - only show if period mode */}
            {hasPeriod && (
                <DaySelector
                    label={t('Marketing.DaysRange')}
                    showOperator
                    operatorOptions={DURATION_OPERATORS_WITHIN_AFTER}
                    value={formik.values.duration}
                    onChange={(next) => formik.setFieldValue('duration', next)}
                    summaryNoteKey="Marketing.DaySelectorNoteCategoryPurchase"
                    summaryParams={{
                        itemSelection:
                            categoryOptions
                                .filter((c) => (formik.values.categoryIds || []).includes(c.value))
                                .map((c) => c.label)
                                .join(', ') || t('Marketing.DaySelectorSelectedCategories'),
                    }}
                    showError={Boolean(
                        formik.touched.duration &&
                            (formik.errors.duration as { hours?: string } | undefined)?.hours,
                    )}
                    error={
                        typeof (formik.errors.duration as { hours?: string } | undefined)?.hours === 'string'
                            ? (formik.errors.duration as { hours?: string }).hours
                            : t('Marketing.DaysRequired')
                    }
                />
            )}
        </div>
    );
};

export default ProductCategoryMultiSelect;
